import React, { ReactElement, useEffect, useState, useMemo } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  useDroppable,
  useDraggable,
  useSensor,
  useSensors,
  PointerSensor
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import {
  Item,
  ItemDefinition,
  ItemValidationError,
  registry,
  RenderProps
} from '../Item';
import {
  DRAG_AND_DROP_CONTEXT_ROOT,
  ROOT,
  LAYOUT_LIST,
  CARD
} from '../itemIds';
import { useEditorStore } from '~/shell/Store';
import styled from 'styled-components';
import DOMPurify from 'dompurify';
import { x } from '@xstyled/styled-components';
import { LayoutList } from '../layout-list/LayoutList';
import { Card } from '../card/Card';
import { Wrapper } from './components/DragDropWrapper';
import { useMediaPlayer } from '~/ui/video-player/useMediaPlayer';
import { decodeHtmlEntities } from '~/lib/decodeHtmlEntities';
import { MediaCaptions } from '~/ui/video-player/MediaCaptions';
import {
  DndImageComponent,
  createImageComponentPreview
} from './dnd-config/components/ImageComponent';
import {
  DndColumnComponent,
  createColumnComponentPreview
} from './dnd-config/components/ColumnComponent';
import { createTextComponentPreview } from './dnd-config/components/TextComponent';
// Palette icons
import ColumnIcon from './dnd-config/assets/column.svg';
import ImageIcon from './dnd-config/assets/Image.svg';
import TextIcon from './dnd-config/assets/text.svg';
import PlusSquareIcon from './dnd-config/assets/Plus_square.svg';

// Shells and visuals
const CanvasShell = styled(x.div)<{ $readOnly?: boolean }>`
  width: 100%;
  height: 100%;
  min-height: 300px;
  background: ${({ $readOnly }) =>
    $readOnly ? 'transparent' : '#f8f1ff'}; /* Root canvas background */
`;

const EmptyCanvas = styled(x.div)<{ $readOnly?: boolean }>`
  width: 100%;
  height: 100%;
  min-height: 300px;
  border: ${({ $readOnly }) =>
    $readOnly
      ? '2px dashed transparent'
      : '2px dashed #9738ff'}; /* Dotted outline color */
  border-radius: 12px;
  display: grid;
  place-items: center;
  color: #666;
  background: ${({ $readOnly }) =>
    $readOnly ? 'transparent' : '#f8f1ff'}; /* Match canvas background */
  padding: 8px;
  text-align: center;
`;

type ImageNode = { id: string; type: 'IMAGE'; subItem?: SeqItemLite };
type TextNode = { id: string; type: 'TEXT'; subItem?: SeqItemLite };
type ContentNode = ImageNode | TextNode;
type ColumnNode = {
  id: string;
  type: 'COLUMN';
  children?: [ColumnNode, ColumnNode];
  content?: ContentNode[];
};
type CanvasNode = ImageNode | ColumnNode;

// Minimal local typing to navigate the sequence tree
type SeqItemLite = {
  id: string | number;
  items?: SeqItemLite[];
  pictures?: Array<{
    id: string | number;
    mediaUrl?: string;
    screenshotUrl?: string;
    name?: string;
  }>;
  title?: string | null;
  body?: string | null;
};
type SequenceLite = {
  id?: string | number;
  items?: SeqItemLite[];
};

const ItemFill = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 300px;
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  padding: 6px 8px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);

  &:hover {
    background: #f6f6f6;
  }
`;

const DroppableCanvas = React.memo(
  ({
    root,
    onDelete,
    onDeleteColumn,
    onDeleteImage,
    readOnly,
    sequenceItems
  }: {
    root: CanvasNode | null;
    onDelete: (id: string) => void; // root image delete
    onDeleteColumn: (id: string) => void;
    onDeleteImage: (id: string) => void;
    readOnly?: boolean;
    sequenceItems?: SeqItemLite[] | undefined;
  }) => {
    const { setNodeRef } = useDroppable({
      id: 'root-canvas',
      // Disable root canvas droppable whenever a root node exists so drops land on column/content targets
      disabled: Boolean(readOnly || root)
    });
    const rootImageSubItem = useMemo(() => {
      if (!root || root.type !== 'IMAGE' || !sequenceItems) return undefined;
      const findById = (
        items: SeqItemLite[] | undefined,
        id: string
      ): SeqItemLite | undefined => {
        if (!items) return undefined;
        for (const it of items) {
          if (String(it?.id) === id) return it;
          const found = findById(it.items, id);
          if (found) return found;
        }
        return undefined;
      };
      return findById(sequenceItems, root.id);
    }, [root, sequenceItems]);
    return (
      <CanvasShell
        ref={setNodeRef}
        data-testid="dndkit:root-canvas"
        $readOnly={!!readOnly}
      >
        {!root ? (
          <EmptyCanvas $readOnly={!!readOnly}>
            {readOnly ? null : 'Drag elements here'}
          </EmptyCanvas>
        ) : (
          <ItemFill>
            {root.type === 'IMAGE' ? (
              <>
                <DndImageComponent
                  subItem={rootImageSubItem}
                  subItemId={root.id}
                />
                {!readOnly && (
                  <DeleteButton
                    type="button"
                    aria-label="Delete image"
                    onClick={() => onDelete(root.id)}
                  >
                    ✕ Delete
                  </DeleteButton>
                )}
              </>
            ) : (
              <DndColumnComponent
                node={root}
                onDeleteColumn={onDeleteColumn}
                onDeleteImage={onDeleteImage}
                readOnly={readOnly}
                sequenceItems={sequenceItems}
              />
            )}
          </ItemFill>
        )}
      </CanvasShell>
    );
  }
);

// Editor-stage arranges canvas and side controls as siblings with a 4px gap
const EditorStage = styled.div`
  position: relative; /* anchor overlay */
  display: grid;
  grid-template-columns: minmax(0, 1fr); /* canvas only; side UI overlays */
  width: 100%;
  height: 100%;
`;

const RightSidebar = styled.div`
  position: absolute; /* overlay above canvas without shifting it */
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 253px;
  height: 685px;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  z-index: 1000; /* above canvas */
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SidebarClose = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  color: #1a1a1a;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
`;

const SidebarTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #1a1a1a;
`;

const SidebarGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr; /* two per row */
  gap: 16px 12px;
  align-items: start;
  justify-items: center; /* center fixed-size tiles */
`;

const PaletteItem = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: grab;
  user-select: none;
  -webkit-user-drag: none;
  &:active {
    cursor: grabbing;
  }
`;

const PaletteTileBox = styled.div`
  width: 106.5px;
  height: 106px;
  border-radius: 8px;
  background: #f8f1ff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PaletteTileIcon = styled.div`
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  & > svg {
    display: block;
  }
`;

const PaletteTileLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #1a1a1a;
  text-align: center;
`;

// Small draggable tiles for the sidebar
const ImagePaletteItem: React.FC = React.memo(() => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: 'tool:IMAGE'
  });
  return (
    <PaletteItem
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      aria-label="Add Image"
      style={{ opacity: isDragging ? 0.6 : 1 }}
    >
      <PaletteTileBox>
        <PaletteTileIcon>
          <ImageIcon aria-hidden />
        </PaletteTileIcon>
      </PaletteTileBox>
      <PaletteTileLabel>Image</PaletteTileLabel>
    </PaletteItem>
  );
});

const ColumnPaletteItem: React.FC = React.memo(() => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: 'tool:COLUMN'
  });
  return (
    <PaletteItem
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      aria-label="Add Column"
      style={{ opacity: isDragging ? 0.6 : 1 }}
    >
      <PaletteTileBox>
        <PaletteTileIcon>
          <ColumnIcon aria-hidden />
        </PaletteTileIcon>
      </PaletteTileBox>
      <PaletteTileLabel>Column</PaletteTileLabel>
    </PaletteItem>
  );
});

const TextPaletteItem: React.FC = React.memo(() => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: 'tool:TEXT'
  });
  return (
    <PaletteItem
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      aria-label="Add Text"
      style={{ opacity: isDragging ? 0.6 : 1 }}
    >
      <PaletteTileBox>
        <PaletteTileIcon>
          <TextIcon aria-hidden />
        </PaletteTileIcon>
      </PaletteTileBox>
      <PaletteTileLabel>Text</PaletteTileLabel>
    </PaletteItem>
  );
});
interface RootValues {
  items?: string;
}
export type DragAndDropContextRootDefinition = ItemDefinition<
  typeof DRAG_AND_DROP_CONTEXT_ROOT,
  void
>;

export class DragAndDropContextRoot extends Item<
  typeof DRAG_AND_DROP_CONTEXT_ROOT,
  RootValues
> {
  protected validate(): void {
    if (this.parent?.type !== ROOT)
      throw new ItemValidationError(
        `An item of type ${DRAG_AND_DROP_CONTEXT_ROOT} must have only ${ROOT} as a parent`
      );
  }

  protected render({ items }: RenderProps<RootValues>): ReactElement | null {
    const { editorMode, contentEditable, sequence, apiService, setSequence } =
      useEditorStore();
    const isEditor = Boolean(contentEditable);
    // Parse learner-mode sequence from values (no store sequence there)
    let valuesSequence: unknown;
    try {
      valuesSequence = items ? JSON.parse(items) : undefined;
    } catch {
      valuesSequence = undefined;
    }

    // Slide narration: mirror AudioHelper behavior but inline for this template.
    // Always call the hook; pass autoplay only for learners (editor editorMode disables in Audio component too).
    type SeqAudioMeta = {
      narration?: string;
      subtitlesUrl?: string;
      playAudio?: boolean;
    };
    const seqForAudio = ((isEditor ? (sequence as unknown) : valuesSequence) as
      | SeqAudioMeta
      | undefined) || {
      narration: undefined,
      subtitlesUrl: undefined,
      playAudio: false
    };
    const narrationUrl = seqForAudio.narration;
    const subtitlesUrl = seqForAudio.subtitlesUrl;
    const autoPlay = isEditor ? false : Boolean(seqForAudio.playAudio);
    useMediaPlayer(narrationUrl, 'audio', subtitlesUrl, autoPlay);

    const [root, setRoot] = useState<CanvasNode | null>(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [activeId, setActiveId] = useState<string | null>(null);
    // Helpers to build/read backend structure (COLUMN only for now)
    // Backend representation stored in the LAYOUT.body JSON.
    // Support both legacy `{ items: { left, right } }` and direct `{ left, right }` shapes.
    type ColumnBackend = {
      id?: string | number;
      type?: string;
      items?: {
        left: ColumnBackend | null;
        right: ColumnBackend | null;
      } | null;
      left?: ColumnBackend | null;
      right?: ColumnBackend | null;
      // Persist image subitems by id in each leaf column
      content?: Array<string | number> | null;
    };

    const normalizeBody = (raw: string | null | undefined): string | null => {
      if (!raw) return null;
      let s = String(raw).trim();
      // Strip wrapping HTML tags like <p> ... </p>
      if (s.startsWith('<')) {
        s = s.replace(/<[^>]*>/g, '');
      }
      // Decode HTML entities like &quot;
      if (s.includes('&')) {
        s = decodeHtmlEntities(s);
      }
      return s;
    };

    const mapBackendToColumnNode = (
      col: ColumnBackend | null | undefined,
      rootItems: SeqItemLite[] | undefined
    ): ColumnNode | null => {
      if (!col) return null;
      const node: ColumnNode = {
        id: String(col.id ?? makeId('col')),
        type: 'COLUMN'
      };
      const leftSrc = col.items?.left ?? col.left ?? null;
      const rightSrc = col.items?.right ?? col.right ?? null;
      const left = leftSrc ? mapBackendToColumnNode(leftSrc, rootItems) : null;
      const right = rightSrc
        ? mapBackendToColumnNode(rightSrc, rootItems)
        : null;
      if (left || right) {
        node.children = [
          left ?? { id: makeId('colL'), type: 'COLUMN' },
          right ?? { id: makeId('colR'), type: 'COLUMN' }
        ];
      }
      if (Array.isArray(col.content) && col.content.length > 0) {
        // Infer node type from sequence data (pictures -> IMAGE, else TEXT)
        const findById = (
          items: SeqItemLite[] | undefined,
          id: string
        ): SeqItemLite | undefined => {
          if (!items) return undefined;
          for (const it of items) {
            if (String(it?.id) === id) return it;
            const found = findById(it.items, id);
            if (found) return found;
          }
          return undefined;
        };
        node.content = col.content.filter(Boolean).map(idRaw => {
          const id = String(idRaw);
          const sub = findById(rootItems, id);
          const hasPics =
            Array.isArray(sub?.pictures) && sub!.pictures!.length > 0;
          // Also treat as IMAGE if body JSON marks it as an image placeholder
          let isImageVariant = false;
          if (sub?.body) {
            try {
              const bodyStr = normalizeBody(sub.body);
              const parsed = bodyStr ? JSON.parse(bodyStr) : null;
              isImageVariant =
                parsed?.dndKind === 'IMAGE' ||
                parsed?.variant === 'IMAGE' ||
                parsed?.isImage === true;
            } catch {
              // ignore parse errors
            }
          }
          return hasPics || isImageVariant
            ? ({ id, type: 'IMAGE', subItem: sub } as ImageNode)
            : ({ id, type: 'TEXT', subItem: sub } as TextNode);
        });
      }
      // If items is null/undefined, treat as leaf column (no children)
      return node;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const buildTreeFromSequence = (): CanvasNode | null => {
      // Find first LAYOUT at top-level (our persisted column root)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const seqAny = sequence ? (sequence as any) : (valuesSequence as any);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const top: any[] = Array.isArray(seqAny?.items) ? seqAny.items : [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const firstLayout = top.find((it: any) => it?.type === 'LAYOUT');
      if (!firstLayout) return null;
      const rawBody = firstLayout?.body as string | null | undefined;
      if (!rawBody) {
        // No body yet: treat as a single leaf column using the layout id as the root id
        return { id: String(firstLayout.id), type: 'COLUMN' };
      }
      try {
        const bodyStr = normalizeBody(rawBody);

        const parsed: ColumnBackend | null = bodyStr
          ? JSON.parse(bodyStr)
          : null;
        const mapped = mapBackendToColumnNode(
          parsed,
          seqAny?.items as SeqItemLite[] | undefined
        );
        if (!mapped) return { id: String(firstLayout.id), type: 'COLUMN' };
        // Force root id to match the layout subitem id for stable persistence
        return {
          id: String(firstLayout.id),
          type: 'COLUMN',
          children: mapped.children,
          content: mapped.content
        };
      } catch {
        return { id: String(firstLayout.id), type: 'COLUMN' };
      }
    };

    useEffect(() => {
      const tree = buildTreeFromSequence();
      setRoot(tree);
    }, [sequence, contentEditable]);

    const toBackendItems = (node: ColumnNode): ColumnBackend['items'] => {
      if (!node.children) return { left: null, right: null };
      const [left, right] = node.children;
      const mapChild = (n: ColumnNode | undefined): ColumnBackend => ({
        id: n?.id,
        type: 'LAYOUT',
        items: n?.children ? toBackendItems(n) : { left: null, right: null },
        content: n?.content?.map(item => item.id) ?? null
      });
      return {
        left: left ? mapChild(left) : null,
        right: right ? mapChild(right) : null
      };
    };

    // Helper to persist full tree to body JSON on root layout
    const persistTreeToBody = async (tree: ColumnNode | null) => {
      try {
        if (!apiService || !tree) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const seqAny = sequence as any;
        const rootLayout = Array.isArray(seqAny?.items)
          ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
            seqAny.items.find((it: any) => it?.type === 'LAYOUT')
          : null;
        const rootId = rootLayout?.id || tree.id;
        // Include top-level content when the root is a single (unsplit) column
        const payload: ColumnBackend = {
          id: rootId,
          type: 'LAYOUT',
          items: toBackendItems(tree),
          content: tree.content?.map(item => item.id) ?? null
        };
        await apiService.updateSubItem(rootId, 'body', JSON.stringify(payload));
        await refreshSequence();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('persistTreeToBody failed', e);
      }
    };

    const findColumnById = (
      node: CanvasNode | null,
      id: string
    ): ColumnNode | null => {
      if (!node) return null;
      if (node.type === 'IMAGE') return null;
      if (node.id === id) return node;
      if (node.children) {
        return (
          findColumnById(node.children[0], id) ||
          findColumnById(node.children[1], id)
        );
      }
      return null;
    };

    // Find the id of the column that contains the given content id
    const findColumnIdByContentId = (
      node: CanvasNode | null,
      contentId: string
    ): string | null => {
      if (!node) return null;
      if (node.type === 'IMAGE') return null;
      if (node.content?.some(c => String(c.id) === String(contentId))) {
        return node.id;
      }
      if (node.children) {
        return (
          findColumnIdByContentId(node.children[0], contentId) ||
          findColumnIdByContentId(node.children[1], contentId)
        );
      }
      return null;
    };

    const refreshSequence = async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const seqId = (sequence as any)?.id;
        if (!apiService || !seqId) return;
        const fresh = await apiService.getSequenceInfo(seqId);
        if (fresh?.sequence) setSequence(fresh.sequence);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('refreshSequence failed', e);
      }
    };

    const makeId = (p: string) =>
      `${p}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

    const splitColumn = (
      node: CanvasNode | null,
      targetId: string
    ): CanvasNode | null => {
      if (!node) return node;
      if (node.type === 'IMAGE') return node;
      if (node.id === targetId) {
        const left: ColumnNode = {
          id: makeId('colL'),
          type: 'COLUMN',
          content: node.content ? [...node.content] : undefined
        };
        const right: ColumnNode = { id: makeId('colR'), type: 'COLUMN' };
        return { ...node, children: [left, right], content: undefined };
      }
      if (node.children) {
        return {
          ...node,
          children: [
            splitColumn(node.children[0], targetId) as ColumnNode,
            splitColumn(node.children[1], targetId) as ColumnNode
          ]
        };
      }
      return node;
    };

    const addContentToColumn = (
      node: CanvasNode | null,
      targetId: string,
      item: ContentNode
    ): CanvasNode | null => {
      if (!node) return node;
      if (node.type === 'IMAGE') return node;
      if (node.id === targetId) {
        const content = node.content ? [...node.content, item] : [item];
        return { ...node, content };
      }
      if (node.children) {
        return {
          ...node,
          children: [
            addContentToColumn(node.children[0], targetId, item) as ColumnNode,
            addContentToColumn(node.children[1], targetId, item) as ColumnNode
          ]
        };
      }
      return node;
    };

    const addContentToColumnAfter = (
      node: CanvasNode | null,
      columnId: string,
      afterContentId: string,
      item: ContentNode
    ): CanvasNode | null => {
      if (!node) return node;
      if (node.type === 'IMAGE') return node;
      if (node.id === columnId) {
        const content = node.content ? [...node.content] : [];
        const idx = content.findIndex(
          c => String(c.id) === String(afterContentId)
        );
        if (idx === -1) {
          // If not found, append to end as a fallback
          return { ...node, content: [...content, item] };
        }
        const next = [
          ...content.slice(0, idx + 1),
          item,
          ...content.slice(idx + 1)
        ];
        return { ...node, content: next };
      }
      if (node.children) {
        return {
          ...node,
          children: [
            addContentToColumnAfter(
              node.children[0],
              columnId,
              afterContentId,
              item
            ) as ColumnNode,
            addContentToColumnAfter(
              node.children[1],
              columnId,
              afterContentId,
              item
            ) as ColumnNode
          ]
        };
      }
      return node;
    };

    const addContentToColumnBefore = (
      node: CanvasNode | null,
      columnId: string,
      beforeContentId: string,
      item: ContentNode
    ): CanvasNode | null => {
      if (!node) return node;
      if (node.type === 'IMAGE') return node;
      if (node.id === columnId) {
        const content = node.content ? [...node.content] : [];
        const idx = content.findIndex(
          c => String(c.id) === String(beforeContentId)
        );
        if (idx === -1) {
          // If not found, prepend as a fallback
          return { ...node, content: [item, ...content] };
        }
        const next = [...content.slice(0, idx), item, ...content.slice(idx)];
        return { ...node, content: next };
      }
      if (node.children) {
        return {
          ...node,
          children: [
            addContentToColumnBefore(
              node.children[0],
              columnId,
              beforeContentId,
              item
            ) as ColumnNode,
            addContentToColumnBefore(
              node.children[1],
              columnId,
              beforeContentId,
              item
            ) as ColumnNode
          ]
        };
      }
      return node;
    };

    // Track pointer position during drag to decide insert position (above/below)
    const lastPointer = React.useRef<{ x: number; y: number } | null>(null);
    const onPointerMove = (e: PointerEvent) => {
      lastPointer.current = { x: e.clientX, y: e.clientY };
    };

    const handleDragStart = (event: DragStartEvent) => {
      if (event?.active?.id) setActiveId(String(event.active.id));
      // Mark global dragging state to allow CSS to pause animations (e.g., EditBtnIcon lift animation)
      try {
        document.body.setAttribute('data-dnd-dragging', 'true');
        window.addEventListener('pointermove', onPointerMove, {
          passive: true
        });
      } catch {
        // no-op
      }
    };

    // Safely extract top/height from `over.rect` without using any-casts.
    const getOverRect = (
      overEl: DragEndEvent['over']
    ): { top: number; height: number } | undefined => {
      const rectContainer = (overEl as unknown as { rect?: unknown })?.rect;
      if (!rectContainer || typeof rectContainer !== 'object') return undefined;
      // Case 1: rect is a plain object with numeric top/height
      const r1 = rectContainer as {
        top?: unknown;
        height?: unknown;
      };
      if (typeof r1.top === 'number' && typeof r1.height === 'number') {
        return { top: r1.top, height: r1.height };
      }
      // Case 2: rect is a ref-like object with current {top,height}
      const r2 = rectContainer as {
        current?: { top?: unknown; height?: unknown } | null;
      };
      if (
        r2.current &&
        typeof r2.current.top === 'number' &&
        typeof r2.current.height === 'number'
      ) {
        return { top: r2.current.top, height: r2.current.height };
      }
      return undefined;
    };

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) {
        setActiveId(null);
        // Clear global dragging state
        try {
          document.body.removeAttribute('data-dnd-dragging');
          window.removeEventListener('pointermove', onPointerMove);
        } catch {
          // no-op
        }
        return;
      }

      const overId = String(over.id);
      const activeKey = String(active.id);

      // Allow dropping COLUMN onto existing content (image/text) by splitting its parent column.
      // Also allow dropping IMAGE/TEXT (new or existing) onto existing content to insert below/above in same column.
      if (overId.startsWith('content:')) {
        const contentId = overId.split(':')[1];
        // Determine insert position based on pointer vs target midline
        const overRect = getOverRect(over);
        const pointerY = lastPointer.current?.y;
        const insertAfter =
          overRect && typeof pointerY === 'number'
            ? pointerY >= overRect.top + overRect.height / 2
            : true; // default to after (below)

        if (activeKey === 'tool:COLUMN') {
          const parentColumnId = findColumnIdByContentId(root, contentId);
          if (parentColumnId) {
            const next = splitColumn(root, parentColumnId);
            setRoot(next);
            // Persist split to backend
            (async () => {
              try {
                if (!apiService) return;
                await persistTreeToBody(next as ColumnNode);
              } catch (e) {
                // eslint-disable-next-line no-console
                console.warn('persist split on content failed', e);
              }
            })();
          }
        } else if (activeKey === 'tool:IMAGE' || activeKey === 'tool:TEXT') {
          const parentColumnId = findColumnIdByContentId(root, contentId);
          if (!parentColumnId) {
            setActiveId(null);
            return;
          }
          // Create backend subitem then insert before/after
          (async () => {
            try {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const seqAny = sequence as any;
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const rootLayout = Array.isArray(seqAny?.items)
                ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (seqAny.items as any[]).find(
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (it: any) => it?.type === 'LAYOUT'
                  )
                : null;
              const parentId = rootLayout?.id;
              if (!apiService || !seqAny?.id || !parentId) return;
              if (activeKey === 'tool:IMAGE') {
                const created = await apiService.addSubItem(seqAny.id, {
                  type: 'TEXT',
                  title: '',
                  parent_id: parentId,
                  body: JSON.stringify({ dndKind: 'IMAGE' })
                });
                const newId = String(created?.subitem?.id ?? makeId('img'));
                setRoot(prev => {
                  const updated = (
                    insertAfter
                      ? addContentToColumnAfter
                      : addContentToColumnBefore
                  )(prev, parentColumnId, contentId, {
                    id: newId,
                    type: 'IMAGE'
                  });
                  persistTreeToBody(updated as ColumnNode);
                  return updated;
                });
              } else if (activeKey === 'tool:TEXT') {
                const created = await apiService.addSubItem(seqAny.id, {
                  type: 'TEXT',
                  title: '',
                  parent_id: parentId
                });
                const newTextId = String(created?.subitem?.id ?? makeId('txt'));
                try {
                  await apiService.updateSubItem(
                    newTextId,
                    'body',
                    'Default text'
                  );
                } catch (e) {
                  // eslint-disable-next-line no-console
                  console.warn(
                    'Failed to set default body for text (content drop)',
                    e
                  );
                }
                setRoot(prev => {
                  const updated = (
                    insertAfter
                      ? addContentToColumnAfter
                      : addContentToColumnBefore
                  )(prev, parentColumnId, contentId, {
                    id: newTextId,
                    type: 'TEXT'
                  });
                  persistTreeToBody(updated as ColumnNode);
                  return updated;
                });
              }
            } catch (e) {
              // eslint-disable-next-line no-console
              console.warn('add content near existing failed', e);
            }
          })();
        } else if (
          activeKey.startsWith('image:') ||
          activeKey.startsWith('text:')
        ) {
          const movingId = activeKey.split(':')[1];
          const parentColumnId = findColumnIdByContentId(root, contentId);
          if (!parentColumnId) {
            setActiveId(null);
            return;
          }
          const moveType: 'IMAGE' | 'TEXT' = activeKey.startsWith('image:')
            ? 'IMAGE'
            : 'TEXT';
          setRoot(prev => {
            if (!prev) return prev;
            const without = removeImageFromTree(prev, movingId);
            if (!without) return without;
            const updated = (
              insertAfter ? addContentToColumnAfter : addContentToColumnBefore
            )(without, parentColumnId, contentId, {
              id: movingId,
              type: moveType
            });
            persistTreeToBody(updated as ColumnNode);
            return updated;
          });
        }
        setActiveId(null);
        return;
      }

      if (overId === 'root-canvas') {
        if (!root) {
          if (activeKey === 'tool:COLUMN') {
            // Optimistic local set
            setRoot({ id: makeId('col'), type: 'COLUMN' });
            // Persist to backend
            (async () => {
              try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const seqId = (sequence as any)?.id;
                if (!apiService || !seqId) return;
                await apiService.addSubItem(seqId, {
                  type: 'LAYOUT',
                  body: JSON.stringify({ left: null, right: null })
                });
                await refreshSequence();
              } catch (e) {
                // eslint-disable-next-line no-console
                console.warn('add root COLUMN failed', e);
              }
            })();
          } else if (activeKey === 'tool:IMAGE') {
            // Create a new root layout and add an image placeholder subitem
            const localColId = makeId('col');
            setRoot({ id: localColId, type: 'COLUMN' });
            (async () => {
              try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const seqAny = sequence as any;
                const seqId = seqAny?.id;
                if (!apiService || !seqId) return;
                // 1) Create layout
                const createdLayout = await apiService.addSubItem(seqId, {
                  type: 'LAYOUT',
                  body: JSON.stringify({ left: null, right: null })
                });
                const layoutId: string | undefined = createdLayout?.subitem?.id
                  ? String(createdLayout.subitem.id)
                  : undefined;
                if (!layoutId) {
                  await refreshSequence();
                }
                // 2) Find the root layout id from current sequence
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const rootLayout = Array.isArray(seqAny?.items)
                  ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    seqAny.items.find((it: any) => it?.type === 'LAYOUT')
                  : null;
                const parentId = layoutId || rootLayout?.id;
                if (!parentId) return;
                // 3) Create a TEXT subitem that marks IMAGE in body (consistent with column flow)
                const createdImgText = await apiService.addSubItem(seqId, {
                  type: 'TEXT',
                  title: '',
                  parent_id: parentId,
                  body: JSON.stringify({ dndKind: 'IMAGE' })
                });
                const newImgId = String(
                  createdImgText?.subitem?.id ?? makeId('img')
                );
                // 4) Update layout body to include content with the new image id
                const payload = {
                  id: parentId,
                  type: 'LAYOUT',
                  items: { left: null, right: null },
                  content: [newImgId]
                };
                await apiService.updateSubItem(
                  parentId,
                  'body',
                  JSON.stringify(payload)
                );
                // Optimistic local UI: add content to local temp root column
                setRoot(
                  prev =>
                    addContentToColumn(prev, localColId, {
                      id: newImgId,
                      type: 'IMAGE'
                    }) as ColumnNode
                );
                await refreshSequence();
              } catch (e) {
                // eslint-disable-next-line no-console
                console.warn('add root IMAGE failed', e);
              }
            })();
          } else if (activeKey === 'tool:TEXT') {
            // Create a new root layout and add a text subitem inside it
            const localColId = makeId('col');
            setRoot({ id: localColId, type: 'COLUMN' });
            (async () => {
              try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const seqAny = sequence as any;
                const seqId = seqAny?.id;
                if (!apiService || !seqId) return;
                // 1) Create layout
                const createdLayout = await apiService.addSubItem(seqId, {
                  type: 'LAYOUT',
                  body: JSON.stringify({ left: null, right: null })
                });
                // Try to resolve layout id: use created response or refreshed sequence
                const layoutId: string | undefined = createdLayout?.subitem?.id
                  ? String(createdLayout.subitem.id)
                  : undefined;
                if (!layoutId) {
                  await refreshSequence();
                  // fall back to finder via existing code path after refresh
                }
                // 2) Find the root layout id from current sequence
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const rootLayout = Array.isArray(seqAny?.items)
                  ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    seqAny.items.find((it: any) => it?.type === 'LAYOUT')
                  : null;
                const parentId = layoutId || rootLayout?.id;
                if (!parentId) return;
                // 3) Create text subitem under layout
                const createdText = await apiService.addSubItem(seqId, {
                  type: 'TEXT',
                  title: '',
                  parent_id: parentId
                });
                const newTextId = String(
                  createdText?.subitem?.id ?? makeId('txt')
                );
                try {
                  await apiService.updateSubItem(
                    newTextId,
                    'body',
                    'Default text'
                  );
                } catch (e) {
                  // eslint-disable-next-line no-console
                  console.warn(
                    'Failed to set default body for text on root',
                    e
                  );
                }
                // 4) Update layout body to include content with the new text id
                const payload = {
                  id: parentId,
                  type: 'LAYOUT',
                  items: { left: null, right: null },
                  content: [newTextId]
                };
                await apiService.updateSubItem(
                  parentId,
                  'body',
                  JSON.stringify(payload)
                );
                // Optimistic local UI: add content to the local temp root column id
                setRoot(
                  prev =>
                    addContentToColumn(prev, localColId, {
                      id: newTextId,
                      type: 'TEXT'
                    }) as ColumnNode
                );
                await refreshSequence();
              } catch (e) {
                // eslint-disable-next-line no-console
                console.warn('add root TEXT failed', e);
              }
            })();
          }
        }
        // Dropping existing items on the root canvas is a no-op
        setActiveId(null);
        return;
      }

      if (overId.startsWith('column:')) {
        const targetId = overId.split(':')[1];
        if (activeKey === 'tool:COLUMN') {
          const next = splitColumn(root, targetId);
          setRoot(next);
          // Persist split to backend: update the root layout's body JSON
          (async () => {
            try {
              if (!apiService) return;
              const target = findColumnById(next, targetId);
              if (target) {
                await persistTreeToBody(next as ColumnNode);
              }
            } catch (e) {
              // eslint-disable-next-line no-console
              console.warn('persist split failed', e);
            }
          })();
        } else if (activeKey === 'tool:IMAGE') {
          // Create backend subitem for image, then update body JSON
          (async () => {
            try {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const seqAny = sequence as any;
              const rootLayout = Array.isArray(seqAny?.items)
                ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  seqAny.items.find((it: any) => it?.type === 'LAYOUT')
                : null;
              const parentId = rootLayout?.id;
              if (!apiService || !seqAny?.id || !parentId) return;
              const created = await apiService.addSubItem(seqAny.id, {
                type: 'TEXT',
                title: '',
                parent_id: parentId,
                body: JSON.stringify({ dndKind: 'IMAGE' })
              });
              const newId = String(created?.subitem?.id ?? makeId('img'));
              setRoot(prev => {
                const updated = addContentToColumn(prev, targetId, {
                  id: newId,
                  type: 'IMAGE'
                });
                // Persist complete tree
                persistTreeToBody(updated as ColumnNode);
                return updated;
              });
            } catch (e) {
              // eslint-disable-next-line no-console
              console.warn('add image failed', e);
            }
          })();
        } else if (activeKey === 'tool:TEXT') {
          // Create backend subitem for text, then update body JSON
          (async () => {
            try {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const seqAny = sequence as any;
              const rootLayout = Array.isArray(seqAny?.items)
                ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  seqAny.items.find((it: any) => it?.type === 'LAYOUT')
                : null;
              const parentId = rootLayout?.id;
              if (!apiService || !seqAny?.id || !parentId) return;
              const created = await apiService.addSubItem(seqAny.id, {
                type: 'TEXT',
                title: '',
                parent_id: parentId
              });
              // Persist default body content for the created subitem (server uses updateSubItem for fields)
              const newTextId: string | undefined = String(
                created?.subitem?.id || ''
              );
              if (newTextId) {
                try {
                  await apiService.updateSubItem(
                    newTextId,
                    'body',
                    'Default text'
                  );
                } catch (e) {
                  // eslint-disable-next-line no-console
                  console.warn('Failed to set default body for text', e);
                }
              }
              const newId = String(created?.subitem?.id ?? makeId('txt'));
              setRoot(prev => {
                const updated = addContentToColumn(prev, targetId, {
                  id: newId,
                  type: 'TEXT'
                });
                // Persist complete tree
                persistTreeToBody(updated as ColumnNode);
                return updated;
              });
            } catch (e) {
              // eslint-disable-next-line no-console
              console.warn('add text failed', e);
            }
          })();
        } else if (activeKey.startsWith('image:')) {
          const imageId = activeKey.split(':')[1];
          // Move existing image to a different column: remove then add
          setRoot(prev => {
            if (!prev) return prev;
            const without = removeImageFromTree(prev, imageId);
            if (!without) return without;
            const img: ImageNode = { id: imageId, type: 'IMAGE' };
            const updated = addContentToColumn(without, targetId, img);
            // Persist move in body JSON
            persistTreeToBody(updated as ColumnNode);
            return updated;
          });
        } else if (activeKey.startsWith('text:')) {
          const textId = activeKey.split(':')[1];
          // Move existing text to a different column: remove then add
          setRoot(prev => {
            if (!prev) return prev;
            const without = removeImageFromTree(prev, textId);
            if (!without) return without;
            const t: TextNode = { id: textId, type: 'TEXT' };
            const updated = addContentToColumn(without, targetId, t);
            // Persist move in body JSON
            persistTreeToBody(updated as ColumnNode);
            return updated;
          });
        }
      }

      setActiveId(null);
      // Clear global dragging state
      try {
        document.body.removeAttribute('data-dnd-dragging');
        window.removeEventListener('pointermove', onPointerMove);
      } catch {
        // no-op
      }
    };

    const handleDragCancel = () => {
      setActiveId(null);
      // Clear global dragging state
      try {
        document.body.removeAttribute('data-dnd-dragging');
        window.removeEventListener('pointermove', onPointerMove);
      } catch {
        // no-op
      }
    };

    const LayoutListComponent = useMemo(() => {
      const layoutListItem = new LayoutList({
        id: 'drag-drop-layout-list',
        type: LAYOUT_LIST,
        values: { align: 'center' as const, padded: true },
        items: []
      });
      return layoutListItem.component();
    }, []);

    const CardComponent = useMemo(() => {
      const cardItem = new Card({
        type: CARD,
        id: 'drag-drop-card',
        values: {
          width: '92%',
          height: '100%',
          padding: { _: '8px', md: '35px' }
        },
        items: []
      });
      return cardItem.component();
    }, []);

    const handleDelete = (id: string) => {
      setRoot(prev =>
        prev && prev.type === 'IMAGE' && prev.id === id ? null : prev
      );
    };

    const pruneColumn = (
      node: CanvasNode | null,
      targetId: string
    ): CanvasNode | null => {
      if (!node) return node;
      if (node.type === 'IMAGE') return node;
      if (node.id === targetId) {
        // remove this column; parent will collapse
        return null;
      }
      if (node.children) {
        const left = pruneColumn(
          node.children[0],
          targetId
        ) as ColumnNode | null;
        const right = pruneColumn(
          node.children[1],
          targetId
        ) as ColumnNode | null;
        if (left && right) return { ...node, children: [left, right] };
        if (left && !right) return left;
        if (!left && right) return right;
        return {
          id: node.id,
          type: 'COLUMN',
          content: node.content ? [...node.content] : []
        };
      }
      return node;
    };

    const removeImageFromTree = (
      node: CanvasNode | null,
      imageId: string
    ): CanvasNode | null => {
      if (!node) return node;
      if (node.type === 'IMAGE') return node; // do not alter root IMAGE here
      // Remove only from content; preserve structure
      const nextContent = node.content?.filter(img => img.id !== imageId);
      const updated: ColumnNode = { ...node, content: nextContent };
      if (node.children) {
        const left = removeImageFromTree(
          node.children[0],
          imageId
        ) as ColumnNode | null;
        const right = removeImageFromTree(
          node.children[1],
          imageId
        ) as ColumnNode | null;
        // Never collapse columns during image deletion; if recursion unexpectedly returns null, keep original child
        (updated as ColumnNode).children = [
          left ?? node.children[0],
          right ?? node.children[1]
        ] as [ColumnNode, ColumnNode];
      }
      return updated;
    };

    const handleDeleteColumn = (columnId: string) => {
      setRoot(prev => {
        const pruned = pruneColumn(prev, columnId);
        // Keep the root id stable when collapsing, so future updates map to the same backend subitem
        let next = pruned;
        if (
          prev &&
          prev.type === 'COLUMN' &&
          pruned &&
          pruned.type === 'COLUMN' &&
          pruned.id !== prev.id
        ) {
          next = {
            id: prev.id,
            type: 'COLUMN',
            children: (pruned as ColumnNode).children,
            content: (pruned as ColumnNode).content
          };
        }

        // Persist to backend (columns only)
        (async () => {
          try {
            if (!apiService) return;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const seqId = (sequence as any)?.id;
            if (!seqId) return;

            if (prev && prev.type === 'COLUMN') {
              if (!next) {
                // Entire root column removed
                await apiService.removeSubItem(prev.id);
                await refreshSequence();
              } else {
                // Update root column's body JSON to reflect new structure
                // Include top-level content as well
                const payload: ColumnBackend = {
                  id: prev.id,
                  type: 'LAYOUT',
                  items: toBackendItems(next as ColumnNode),
                  content:
                    (next as ColumnNode).content?.map(img => img.id) ?? null
                };
                await apiService.updateSubItem(
                  prev.id,
                  'body',
                  JSON.stringify(payload)
                );
                await refreshSequence();
              }
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('persist column delete failed', e);
          }
        })();

        return next;
      });
    };

    const handleDeleteImage = (imageId: string) => {
      setRoot(prev => {
        const updated = removeImageFromTree(prev, imageId);
        // Persist body JSON
        persistTreeToBody(updated as ColumnNode);
        return updated;
      });
      // Also remove the subitem itself
      (async () => {
        try {
          if (!apiService) return;
          await apiService.removeSubItem(imageId);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('remove image subitem failed', e);
        }
      })();
    };

    const renderOverlay = () => {
      if (!activeId) return null;
      if (activeId === 'tool:IMAGE') {
        return (
          <OverlayContainer>{createImageComponentPreview()}</OverlayContainer>
        );
      }
      if (activeId === 'tool:COLUMN') {
        return (
          <OverlayContainer>{createColumnComponentPreview()}</OverlayContainer>
        );
      }
      if (activeId === 'tool:TEXT') {
        return (
          <OverlayContainer>{createTextComponentPreview()}</OverlayContainer>
        );
      }
      if (activeId.startsWith('image:')) {
        const subItemId = activeId.split(':')[1];
        // Recursively find the subitem anywhere in the tree
        const findById = (
          items: SeqItemLite[] | undefined,
          id: string
        ): SeqItemLite | undefined => {
          if (!items) return undefined;
          for (const it of items) {
            if (String(it?.id) === id) return it;
            if (it?.items) {
              const found = findById(it.items, id);
              if (found) return found;
            }
          }
          return undefined;
        };
        const overlayItems = isEditor
          ? (sequence as SequenceLite | undefined)?.items
          : (valuesSequence as SequenceLite | undefined)?.items;
        const imageItem = findById(overlayItems, String(subItemId));
        const pictureData = imageItem?.pictures?.[0];
        // Prefer mediaUrl but fall back to screenshotUrl
        const imageUrl = pictureData?.mediaUrl || pictureData?.screenshotUrl;
        const alt = pictureData?.name || 'Image';
        return (
          <OverlayContainer>
            {imageUrl ? (
              <OverlayImage src={imageUrl} alt={alt} />
            ) : (
              <OverlayPlaceholder>📷 No image</OverlayPlaceholder>
            )}
          </OverlayContainer>
        );
      }
      if (activeId.startsWith('text:')) {
        const subItemId = activeId.split(':')[1];
        const findById = (
          items: SeqItemLite[] | undefined,
          id: string
        ): SeqItemLite | undefined => {
          if (!items) return undefined;
          for (const it of items) {
            if (String(it?.id) === id) return it;
            const found = findById(it.items, id);
            if (found) return found;
          }
          return undefined;
        };
        const overlayItems = isEditor
          ? (sequence as SequenceLite | undefined)?.items
          : (valuesSequence as SequenceLite | undefined)?.items;
        const textItem = findById(overlayItems, String(subItemId));
        const raw = (textItem?.body as string) || 'Default text';
        // Sanitize potential HTML in the text body for safe preview rendering
        const sanitized = DOMPurify.sanitize(raw ?? '', {
          USE_PROFILES: { html: true }
        });
        return (
          <OverlayContainer>
            <OverlayPlaceholder>
              <div
                style={{ width: '100%', padding: '8px', textAlign: 'left' }}
                dangerouslySetInnerHTML={{
                  __html:
                    sanitized && sanitized.trim().length > 0
                      ? sanitized
                      : 'Default text'
                }}
              />
            </OverlayPlaceholder>
          </OverlayContainer>
        );
      }
      return null;
    };

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: { distance: 0 }
      })
    );

    const sequenceItemsForRender = useMemo(() => {
      return (
        isEditor
          ? (sequence as SequenceLite | undefined)?.items
          : (valuesSequence as SequenceLite | undefined)?.items
      ) as SeqItemLite[] | undefined;
    }, [isEditor, sequence, valuesSequence]);
    if (isEditor) {
      return (
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          modifiers={[snapCenterToCursor]}
        >
          <Wrapper>
            <EditorStage>
              <LayoutListComponent>
                <CardComponent>
                  <DroppableCanvas
                    root={root}
                    onDelete={handleDelete}
                    onDeleteColumn={handleDeleteColumn}
                    onDeleteImage={handleDeleteImage}
                    sequenceItems={sequenceItemsForRender}
                  />
                </CardComponent>
              </LayoutListComponent>
              {/* Side column */}
              {isSidebarOpen ? (
                <RightSidebar role="region" aria-label="Drag elements to slide">
                  <SidebarClose
                    type="button"
                    aria-label="Close sidebar"
                    onClick={() => setIsSidebarOpen(false)}
                  >
                    ✕
                  </SidebarClose>
                  <SidebarTitle>Drag elements to slide</SidebarTitle>
                  <SidebarGrid>
                    <ColumnPaletteItem />
                    <ImagePaletteItem />
                    <TextPaletteItem />
                  </SidebarGrid>
                </RightSidebar>
              ) : (
                <SideSquare
                  aria-hidden
                  role="button"
                  tabIndex={0}
                  onClick={() => setIsSidebarOpen(true)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setIsSidebarOpen(true);
                    }
                  }}
                >
                  <SideSquareInner>
                    <PlusSquareIcon aria-hidden />
                  </SideSquareInner>
                </SideSquare>
              )}
            </EditorStage>
            <DragOverlay
              adjustScale={false}
              dropAnimation={null}
              modifiers={[snapCenterToCursor]}
            >
              {renderOverlay()}
            </DragOverlay>
          </Wrapper>
        </DndContext>
      );
    }

    // Learners mode: render the same layout and sizing without DnD/palette
    return (
      <Wrapper>
        <LayoutListComponent>
          <CardComponent>
            <DroppableCanvas
              root={root}
              onDelete={handleDelete}
              onDeleteColumn={handleDeleteColumn}
              onDeleteImage={handleDeleteImage}
              readOnly
              sequenceItems={sequenceItemsForRender}
            />
            {/* Render captions if available (audio may have subtitles) */}
            <MediaCaptions />
          </CardComponent>
        </LayoutListComponent>
      </Wrapper>
    );
  }
}

registry[DRAG_AND_DROP_CONTEXT_ROOT] = DragAndDropContextRoot;

// Overlay visuals for drag preview
const OverlayContainer = styled.div`
  width: 240px;
  height: 160px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grabbing;
  pointer-events: none;
`;

const OverlayImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #fff;
`;

const OverlayPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 14px;
  background: #fafafa;
  border: 2px dashed #9738ff; /* Dotted outline color */
`;

// Editor-only square (closed state) as a sibling to the canvas
const SideSquare = styled.div`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  width: 58px;
  height: 58px;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  cursor: pointer;
  display: grid;
  place-items: center; /* center inner 38x38 square */
  z-index: 10; /* below sidebar but above canvas */
`;

const SideSquareInner = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 8px;
  background: #f8f1ff;
  display: flex;
  align-items: center;
  justify-content: center;
  & > svg {
    display: block;
  }
`;
